(() => {
  const PredictiveSearch = {
    config: {
      debounceDelay: 200,
      minChars: 1,
      searchInput: null,
      resultsContainer: null,
      form: null,
      abortController: null,
    },

    checkDOMElements() {
      const form = document.querySelector('form[role="search"]') || document.querySelector('.search-form');
      const input = form?.querySelector('input[type="search"]') || document.querySelector('input[type="search"]');
      const resultsContainer =
        document.querySelector('[data-predictive-results]') ||
        document.querySelector('.predictive-search__results');

      return {
        form,
        input,
        resultsContainer,
        hasElements: !!(input && resultsContainer),
      };
    },

    init() {
      this.log('Predictive search init starting');

      const { form, input, resultsContainer, hasElements } = this.checkDOMElements();

      if (!hasElements) {
        this.log('No search input or results container found, skipping predictive search');
        return;
      }

      this.config.form = form;
      this.config.searchInput = input;
      this.config.resultsContainer = resultsContainer;

      input.setAttribute('autocomplete', 'off');
      input.setAttribute('aria-expanded', 'false');
      if (!resultsContainer.id) resultsContainer.id = 'predictive-results';
      input.setAttribute('aria-controls', resultsContainer.id);

      this.log('Predictive search initialized');

      input.addEventListener('input', this.debounce(this.onInput.bind(this), this.config.debounceDelay));
      input.addEventListener('keydown', this.onKeyDown.bind(this));
      input.addEventListener('blur', this.onBlur.bind(this));
      document.addEventListener('click', this.onClickOutside.bind(this));
    },

    debounce(fn, delay) {
      let timeoutId;
      return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
      };
    },

    onInput(event) {
      const query = event.target.value.trim();

      if (query.length < this.config.minChars) {
        this.close();
        return;
      }

      this.fetchPredictiveResults(query);
    },

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    },

    onBlur() {
      setTimeout(() => {
        if (!this.config.resultsContainer.contains(document.activeElement)) {
          this.close();
        }
      }, 150);
    },

    onClickOutside(event) {
      const searchForm = this.config.form;
      const resultsContainer = this.config.resultsContainer;

      if (!searchForm || !resultsContainer) return;

      if (!searchForm.contains(event.target) && !resultsContainer.contains(event.target)) {
        this.close();
      }
    },

    fetchPredictiveResults(query) {
      if (this.config.abortController) {
        this.config.abortController.abort();
      }

      this.config.abortController = new AbortController();

      const baseUrl = window.routes?.predictive_search_url || '/search/suggest';
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,article',
        'resources[limit]': 10,
      });

      const url = `${baseUrl}?${params.toString()}`;

      this.log(`Fetching from: ${url}`);

      fetch(url, {
        signal: this.config.abortController.signal,
        headers: {
          'Accept': 'text/html,application/json',
        },
      })
        .then(async (response) => {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            return response.json();
          }
          return response.text();
        })
        .then((data) => this.onFetchSuccess(data, query))
        .catch((error) => {
          if (error.name !== 'AbortError') {
            this.log(`Fetch error: ${error.message}`);
          }
        });
    },

    onFetchSuccess(data, query) {
      this.log('Predictive results received');

      if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
        this.renderNoResults();
        return;
      }

      if (typeof data === 'string') {
        this.config.resultsContainer.innerHTML = data;
      } else {
        this.renderResults(data, query);
      }

      this.open();

      document.dispatchEvent(new CustomEvent('search:predictive:results', {
        detail: { query, results: data },
      }));
    },

    renderNoResults() {
      this.config.resultsContainer.innerHTML = '<div class="predictive-search__no-results">No results found</div>';
      this.open();
    },

    renderResults(data) {
      let html = '<ul class="predictive-search__list">';

      if (data.products && Array.isArray(data.products)) {
        data.products.forEach((product) => {
          html += `
          <li class="predictive-search__item" role="option">
            <a href="${product.url}" class="predictive-search__link">
              <span class="predictive-search__title">${product.title}</span>
              ${product.image ? `<img src="${product.image.src}" alt="${product.image.alt}" class="predictive-search__image" />` : ''}
            </a>
          </li>
        `;
        });
      }

      if (data.articles && Array.isArray(data.articles)) {
        data.articles.forEach((article) => {
          html += `
          <li class="predictive-search__item" role="option">
            <a href="${article.url}" class="predictive-search__link">
              <span class="predictive-search__title">${article.title}</span>
            </a>
          </li>
        `;
        });
      }

      html += '</ul>';
      this.config.resultsContainer.innerHTML = html;
    },

    open() {
      this.config.searchInput.setAttribute('aria-expanded', 'true');
      this.config.resultsContainer.style.display = 'block';

      this.log('Predictive search opened');
      document.dispatchEvent(new CustomEvent('search:predictive:open'));
    },

    close() {
      this.config.searchInput.setAttribute('aria-expanded', 'false');
      this.config.resultsContainer.style.display = 'none';

      this.log('Predictive search closed');
      document.dispatchEvent(new CustomEvent('search:predictive:close'));
    },

    log(message) {
      if (window.devLog) {
        window.devLog('PredictiveSearch', message);
      }
    },
  };

  const predictiveEnabled = window.SA_THEME?.predictiveSearchEnabled === true;
  if (!predictiveEnabled) {
    if (window.devLog) {
      window.devLog('Theme', 'Predictive search disabled via toggle');
    }
    return;
  }

  const init = () => {
    if (window.initOnce) {
      window.initOnce('global-predictive-search', () => {
        PredictiveSearch.init();
      });
    } else {
      PredictiveSearch.init();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
